import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../app/store'
import UserService, { IUserModel } from '../services/user'
import AdminSidebar from '../features/admin/components/Sidebar/AdminSidebar'
import AdminHeader from '../features/admin/components/AdminHeader'
import PageHeadSection from '../features/admin/components/PageHeadSection'
import { useDispatch } from 'react-redux'
import { updateShowSideModal, updateUser } from '../features/user/userSlice'
import GenerateIcon, { IconTypes } from '../components/GenerateIcon'
import { useNavigate, useParams } from 'react-router-dom'
import SectionBoxContainer from '../components/SectionBoxContainer'
import { Avatar } from '@mui/material'
import DynamicForm, { IDynamicForm, IRequiredField } from '../components/Form/DynamicForm'
import { addNewForm, createOrUpdateElementData, updateFormLoading } from '../components/Form/formsSlice'
import GenerateForm from '../components/Form/GenerateForm'
import FormActionButtonsSection from '../components/Form/FormActionButtonsSection'
import { LoadingButton } from '@mui/lab'
import { IAxiosDefaultDataObject } from '../config'
import { updateQuickNotificationDetails } from '../features/notifications/notificationsSlice'
import { verifyStrongPassword } from '../features/user/UsersApp'
import Enable2FAButton from '../features/user/Enable2FAButton'
import Page from './components/Page'
import Breadcrumbs, { IBreadcrumb } from '../components/Breadcrumbs'

interface IProps {
    user: IUserModel
}

/**
 * Genetal
 */
const ProfileGeneral = (props: IProps) => {

    const dispatch = useDispatch()

    const { user } = props
    const [loading, setLoading] = useState<boolean>(false)

    const elemPrefix: string = "user-general-form_"
    const form: IDynamicForm = {
        name: "user-general-form",
        elements: [
            {
                id: elemPrefix + "first_name",
                xs: 6,
                type: "TextField",
                label: "First name",
                fullWidth: true
            },
            {
                id: elemPrefix + "last_name",
                xs: 6,
                type: "TextField",
                label: "Last name",
                fullWidth: true
            },
            {
                id: elemPrefix + "email",
                xs: 12,
                type: "TextField",
                label: "Email",
                fullWidth: true
            },
            {
                id: elemPrefix + "thumbnail",
                xs: 12,
                type: "ImageUploader",
                label: "Thumbnail",
                fullWidth: true
            },
        ]
    }

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(elemPrefix + name, elementsData).value

    /**
     * Update loading
     */
    const updateLoading = (status: boolean) => {
        dispatch(updateFormLoading({ formName: form.name, loading: status }))
        setLoading(status)
    }

    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: user[elem.id.replace(elemPrefix, "") as keyof IUserModel] || ""
        })))
    }

    /**
     * Handle submit
     */
    const handleSubmit = () => {

        let data: IAxiosDefaultDataObject = {
            first_name: singleElemDataValue("first_name"),
            last_name: singleElemDataValue("last_name"),
            email: singleElemDataValue("email"),
            thumbnail: singleElemDataValue("thumbnail")
        }

        let requiredFields: IRequiredField[] = [
            {
                id: elemPrefix + "first_name",
                message: "First name is required"
            },
            {
                id: elemPrefix + "last_name",
                message: "Last name is required"
            },
            {
                id: elemPrefix + "email",
                message: "Email is required"
            },
        ]

        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            updateLoading(true)

            UserService.updateCurrentUser(data.first_name, data.last_name, data.email, data.thumbnail)
                .then(response => {
                    updateLoading(false)

                    const message = response.data.message ? response.data.message : "Success!"

                    updateQuickNotificationDetails(true, message, "success", dispatch)

                    dispatch(updateUser({
                        ...user,
                        first_name: data.first_name,
                        last_name: data.last_name,
                        email: data.email,
                        thumbnail: data.thumbnail
                    }))


                })
                .catch(error => {
                    updateLoading(false)

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)

                })

        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    /**
     * Generate form
     */
    useEffect(() => {
        
        dispatch(addNewForm(form))
        createFormElemData()

    }, [])

    return(
        <div>
            <SectionBoxContainer showTitle title="Profile Details">
                <GenerateForm name={form.name} />
            </SectionBoxContainer>
            <FormActionButtonsSection>
                <LoadingButton loading={loading} className="general-button submit-style" variant="contained" onClick={() => handleSubmit()} >Update</LoadingButton>
            </FormActionButtonsSection>
        </div>
    )
}

/**
 * Security
 */
const ProfileSecurity = (props: IProps) => {

    const dispatch = useDispatch()

    const { user } = props
    const [loading, setLoading] = useState<boolean>(false)

    const elemPrefix: string = "user-password-update-form_"
    const form: IDynamicForm = {
        name: "user-password-form",
        elements: [
            {
                id: elemPrefix + "password",
                xs: 12,
                type: "TextField",
                label: "New Password",
                fullWidth: true,
                fieldType: "password",
                enableStrongPassword: true
            },
        ]
    }

    const elementsData = DynamicForm.useReturnAllFormsData()
    const singleElemDataValue = (name: string) => DynamicForm.returnSingleFormElementData(elemPrefix + name, elementsData).value

    /**
     * Update loading
     */
    const updateLoading = (status: boolean) => {
        dispatch(updateFormLoading({ formName: form.name, loading: status }))
        setLoading(status)
    }

    /**
     * Create form elem data
     */
    const createFormElemData = () => {
        form.elements.map((elem) => dispatch(createOrUpdateElementData({
            name: elem.id,
            value: user[elem.id.replace(elemPrefix, "") as keyof IUserModel] || ""
        })))
    }

    /**
     * Handle submit
     */
    const handleSubmit = () => {

        let data: IAxiosDefaultDataObject = {
            password: singleElemDataValue("password"),
        }

        const isPasswordStrong = (password: string) => {
            const verify = verifyStrongPassword(password)

            if (!verify.lowercase){
                return false
            }

            if (!verify.uppercase){
                return false
            }

            if (!verify.numbers){
                return false
            }

            if (!verify.special_character){
                return false
            }

            if (!verify.length){
                return false
            }

            if (!verify.valid){
                return false
            }

            return true
        }

        let requiredFields: IRequiredField[] = [
            {
                id: elemPrefix + "password",
                message: !isPasswordStrong(data.password) ? "Password is not strong enough!" : "Password is required",
                customValidate: (val) => {

                    if (!val){
                        return false
                    }

                    if (!isPasswordStrong(val)){
                        return false
                    }

                    return true
                },
            },
        ]

        const verifyForm = DynamicForm.verifyForm(elementsData, requiredFields)

        if (verifyForm.isValid){
            updateLoading(true)

            UserService.updateCurrentUserPassword(data.password)
                .then(response => {
                    updateLoading(false)

                    const message = response.data.message ? response.data.message : "Success!"

                    updateQuickNotificationDetails(true, message, "success", dispatch)


                })
                .catch(error => {
                    updateLoading(false)

                    console.log(error)
                    const message = error.response.data.message ? error.response.data.message : "Fatal Error"

                    DynamicForm.createOrUpdateFormMessage(form.name, { shown: true, type: "error", message: message }, dispatch)

                })

        }else{
            DynamicForm.updateRequiredFieldsHelperText(verifyForm.fields, elementsData, dispatch)
        }

    }

    /**
     * Generate form
     */
    useEffect(() => {
        
        dispatch(addNewForm(form))
        createFormElemData()

    }, [])

    return(
        <div>
            <SectionBoxContainer showTitle title="Update Password">
                <GenerateForm name={form.name} />
            </SectionBoxContainer>
            <FormActionButtonsSection>
                <LoadingButton loading={loading} className="general-button submit-style" variant="contained" onClick={() => handleSubmit()} >Update</LoadingButton>
            </FormActionButtonsSection>
        </div>
    )
}

/**
 * 2FA
 */
const Profile2FA = (props: IProps) => {

    const { user } = props

    return(
        <SectionBoxContainer showTitle title="2FA">
            <Enable2FAButton user={user} />
        </SectionBoxContainer>
    )
}

/**
 * Step interface
 */
interface IProfileStep {
    name: string,
    icon: IconTypes,
    title: string,
    component: React.FC<IProps>
}

/**
 * Profile content
 */
const ProfileContent = (props: IProps) => {

    const { user } = props
    const params = useParams()
    const navigate = useNavigate()

    const currentSection = params.section ? params.section : "general"
    const [activateStep, setActiveStep] = useState<string>("general")

    const [stepLineLeft, setStepLineLeft] = useState<number>(0)
    const [stepLineWidth, setStepLineWidth] = useState<number>(0)

    const steps: IProfileStep[] = [
        {
            name: "general",
            icon: "profile",
            title: "General",
            component: ProfileGeneral
        },
        {
            name: "security",
            icon: "security",
            title: "Security",
            component: ProfileSecurity
        },
        {
            name: "two-factor-auth",
            icon: "lock",
            title: "Two-Factor Authentication",
            component: Profile2FA
        }
    ]

    useEffect(() => {
        if (activateStep){
            const elem = document.getElementById("step-" + activateStep)
            if (elem){
                setStepLineWidth(elem.clientWidth)
                setStepLineLeft(elem.offsetLeft)
            }
        }
    }, [activateStep])

    useEffect(() => {
        if (currentSection){
            setActiveStep(currentSection)
        }
    }, [currentSection])

    return(
        <div className="user-profile-page-container">
            <div className="content-of-user-profile-page-container">

                <div className="steps-of-user-profile-page-container">
                    <div className="content-of-steps-of-user-profile-page-container">
                        {steps.map((step, index) => {

                            return(
                                <div id={"step-" + step.name} onClick={() => {
                                    setActiveStep(step.name)
                                    navigate("/profile/" + step.name)
                                }} className={currentSection === step.name ? "single-step-of-user-profile active" : "single-step-of-user-profile"} key={index}>
                                    <div className="content-of-single-step-of-user-profile">
                                        <GenerateIcon icon={step.icon} />
                                        <p>{step.title}</p>
                                    </div>
                                </div>
                            )
                        })}
                        <div className="step-line" style={{ left: stepLineLeft, width: stepLineWidth }}></div>
                    </div>
                </div>

                <div className="active-step-content-container">
                    <div className="content-of-active-step-content-container">
                        {steps.map((step) => {
                            if (step.name === currentSection){
                                return <step.component user={user} />
                            }

                            return <></>
                        })}
                    </div>
                </div>

            </div>
        </div>
    )
}

export default function Profile() {

    const dispatch = useDispatch()

    const user = useSelector((state: RootState) => state.user.details)

    /**
     * Breadcrumbs
     */
    const breadcrumbs: IBreadcrumb[] = [
        {
            title: "Home",
            link: "/"
        },
        {
            title: "Profile",
        }
    ]

    useEffect(() => {
        dispatch(updateShowSideModal(false))
    }, [])

    /**
     * With admin panel
     */
    if (user.is_superuser){
        return(
            <div className="admin-panel-page-main-wrapper">

                <AdminSidebar />
                <AdminHeader />

                <div className="single-admin-page-side-content">
                    <div className="content-of-single-admin-page-side-content">

                        <PageHeadSection pageName="users" customPageType="view" customTitle={user.full_name} />

                        <ProfileContent user={user} />
                    </div>
                </div>

            </div>
        )
    }

    return(
        <Page>
            <div className="content-section-wrapper page-container-wrapper">

                <div className="page-container-head-box">
                    <div className="content-of-page-container-head-box">
                        <h1>{user.full_name}</h1>

                        <Breadcrumbs breadcrumbs={breadcrumbs} />
                    </div>
                </div>

                <div className="profile-page-content-section">
                    <ProfileContent user={user} />
                </div>
            </div>
        </Page>
    )
}